/**
 * WidgetEngine -- Manages widget rendering, ordering, persistence, and 3-dot menus.
 *
 * Loads layout from localStorage, renders widgets in order, provides per-widget
 * controls (move up/down, compact/expanded, hide), and supports presets.
 */

import type { Widget, WidgetConfig, WidgetContext, WidgetPreset } from './types';

const LS_LAYOUT_KEY = 'aweni_home_layout';

export class WidgetEngine {
  private widgets: Map<string, Widget> = new Map();
  private layout: WidgetConfig[] = [];
  private context: WidgetContext;
  private container: HTMLElement | null = null;
  private activeMenuId: string | null = null;
  private documentClickHandler: ((e: Event) => void) | null = null;
  /** DOM element + render timestamp keyed by widget id, module-scoped cache. */
  private _renderCache: Map<string, { wrapper: HTMLElement; ts: number }> = new Map();
  /** Anticipation scores keyed by widget id. Higher score = bumped up in
   *  the feed for the current context. Manually-pinned widgets (size or
   *  user moves) keep the user choice — scores are a tiebreaker for
   *  default-placement widgets only. Set via setAnticipationScores(). */
  private _anticipation: Map<string, number> = new Map();

  constructor(context: WidgetContext) {
    this.context = context;
    this.loadLayout();
  }

  register(widget: Widget): void {
    this.widgets.set(widget.id, widget);
    // Add to layout if not already present
    if (!this.layout.find((c) => c.id === widget.id)) {
      this.layout.push({
        id: widget.id,
        visible: widget.defaultVisible,
        order: widget.defaultOrder,
        size: widget.defaultSize,
      });
    }
  }

  /** Invalidate the render cache so the next render() call rebuilds the
   *  wrapper from scratch instead of reusing the (potentially stale) DOM.
   *  Pass specific ids to target a subset; omit to clear everything. Used
   *  when external state (location, currency, language) makes a widget's
   *  cached output wrong even though the widget itself didn't change. */
  invalidateCache(ids?: string[]): void {
    if (!ids || ids.length === 0) {
      this._renderCache.clear();
      return;
    }
    for (const id of ids) this._renderCache.delete(id);
  }

  /** Set anticipation scores per widget id. Higher = float to top.
   *  Pass an empty map to clear. Re-run render() to apply visually. */
  setAnticipationScores(scores: Map<string, number> | Record<string, number>): void {
    this._anticipation.clear();
    if (scores instanceof Map) {
      scores.forEach((v, k) => this._anticipation.set(k, v));
    } else {
      Object.entries(scores).forEach(([k, v]) => this._anticipation.set(k, v));
    }
  }

  render(container: HTMLElement): void {
    this.container = container;

    // Sort by (anticipation desc, order asc). Order remains the secondary
    // tiebreaker so widgets with neutral score fall back to declared order.
    const sorted = [...this.layout].sort((a, b) => {
      const sa = this._anticipation.get(a.id) ?? 0;
      const sb = this._anticipation.get(b.id) ?? 0;
      if (sa !== sb) return sb - sa;
      return a.order - b.order;
    });

    // Collect visible widgets to render
    const visible: Array<{ config: WidgetConfig; widget: Widget }> = [];
    for (const config of sorted) {
      const widget = this.widgets.get(config.id);
      if (!widget) continue;
      if (widget.visibilityRule && !widget.visibilityRule()) continue;
      if (!config.visible) continue;
      visible.push({ config, widget });
    }

    // Detach fresh cached wrappers before clearing container
    const CACHE_TTL = 60_000;
    const now = Date.now();
    for (const { config } of visible) {
      const cached = this._renderCache.get(config.id);
      if (cached && now - cached.ts < CACHE_TTL) {
        if (cached.wrapper.parentElement) {
          cached.wrapper.parentElement.removeChild(cached.wrapper);
        }
      }
    }
    container.innerHTML = '';

    // First 3 above-the-fold: render synchronously
    const SYNC_THRESHOLD = 3;
    const deferred: Array<{ config: WidgetConfig; widget: Widget; idx: number }> = [];

    visible.forEach((item, idx) => {
      if (idx < SYNC_THRESHOLD) {
        this._renderOne(item.config, item.widget, idx, container);
      } else {
        deferred.push({ ...item, idx });
      }
    });

    // Widgets 4+: one per idle callback tick to avoid blocking the main thread
    if (deferred.length > 0) {
      let i = 0;
      const scheduleNext = (): void => {
        if (i >= deferred.length) return;
        const { config, widget, idx } = deferred[i++];
        this._renderOne(config, widget, idx, container);
        this._scheduleIdle(scheduleNext);
      };
      this._scheduleIdle(scheduleNext);
    }

    // Close menu on outside click
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    this.documentClickHandler = () => this.closeMenu();
    document.addEventListener('click', this.documentClickHandler);
  }

  /**
   * Renders (or reuses cached DOM for) a single widget and appends it to the container.
   */
  private _renderOne(
    config: WidgetConfig,
    widget: Widget,
    idx: number,
    container: HTMLElement,
  ): void {
    const CACHE_TTL = 60_000;
    const now = Date.now();
    const cached = this._renderCache.get(config.id);

    if (cached && now - cached.ts < CACHE_TTL) {
      // Reuse cached wrapper — just re-append; no re-render needed
      container.appendChild(cached.wrapper);
      // Animate in from current opacity/transform state (already visible from last render)
      this._animateIn(cached.wrapper, Math.min(idx, 6) * 24);
      return;
    }

    const wrapper = this._buildWrapperEl(config);
    const content = wrapper.querySelector('.widget-content') as HTMLElement;
    container.appendChild(wrapper);
    this._safeRender(widget, content, config);
    this._renderCache.set(config.id, { wrapper, ts: now });
    this._animateIn(wrapper, Math.min(idx, 6) * 24);
  }

  /** Creates the wrapper div (with optional 3-dot menu) for a widget. */
  private _buildWrapperEl(config: WidgetConfig): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'widget-wrapper mb-4 relative';
    wrapper.dataset.widgetId = config.id;
    wrapper.style.cssText = 'opacity:0;transform:translateY(12px);';

    if (config.id !== 'greeting') {
      const menuBtn = document.createElement('button');
      menuBtn.className = 'widget-menu-btn';
      menuBtn.setAttribute('aria-label', this.context.t('widget_menu'));
      menuBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>`;
      menuBtn.style.cssText = `
        position:absolute;top:8px;${this.context.isRtl ? 'left' : 'right'}:8px;z-index:10;
        width:28px;height:28px;border-radius:8px;border:none;cursor:pointer;
        display:flex;align-items:center;justify-content:center;
        background:transparent;color:var(--text-secondary);
        transition:background 200ms;
      `;
      menuBtn.addEventListener('mouseenter', () => {
        menuBtn.style.background = 'var(--bg-secondary)';
      });
      menuBtn.addEventListener('mouseleave', () => {
        if (this.activeMenuId !== config.id) {
          menuBtn.style.background = 'transparent';
        }
      });
      menuBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMenu(config.id, menuBtn);
      });
      wrapper.appendChild(menuBtn);
    }

    const content = document.createElement('div');
    content.className = 'widget-content';
    wrapper.appendChild(content);

    return wrapper;
  }

  /** Calls widget.render() in a try/catch so one broken widget can't block others. */
  private _safeRender(widget: Widget, content: HTMLElement, config: WidgetConfig): void {
    try {
      widget.render(content, config.size, this.context);
    } catch (_err) {
      content.innerHTML = `<div class="rounded-xl p-4 text-center text-sm" style="color:var(--text-secondary);background:var(--bg-secondary);">${this.context.t('widget_error')}</div>`;
    }
  }

  /** Schedules a staggered entrance animation after `delayMs`. */
  private _animateIn(wrapper: HTMLElement, delayMs: number): void {
    setTimeout(() => {
      wrapper.style.transition = 'opacity 250ms ease, transform 250ms ease';
      wrapper.style.opacity = '1';
      wrapper.style.transform = 'translateY(0)';
    }, delayMs);
  }

  /** Defers `fn` to the next idle period (rAF fallback for environments without rIC). */
  private _scheduleIdle(fn: () => void): void {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(fn, { timeout: 1500 });
    } else {
      requestAnimationFrame(fn);
    }
  }

  private toggleMenu(widgetId: string, anchor: HTMLElement): void {
    if (this.activeMenuId === widgetId) {
      this.closeMenu();
      return;
    }
    this.closeMenu();
    this.activeMenuId = widgetId;

    const config = this.layout.find((c) => c.id === widgetId);
    if (!config) return;

    const menu = document.createElement('div');
    menu.className = 'widget-popup-menu';
    menu.style.cssText = `
      position:absolute;top:36px;${this.context.isRtl ? 'left' : 'right'}:8px;z-index:20;
      background:var(--card-bg);border:1px solid var(--border);border-radius:12px;
      box-shadow:0 8px 24px rgba(0,0,0,0.15);min-width:180px;overflow:hidden;
      animation:widgetMenuIn 150ms ease;
    `;

    const items = [
      {
        label: this.context.t('widget_move_up'),
        icon: '↑',
        action: () => this.moveWidget(widgetId, -1),
      },
      {
        label: this.context.t('widget_move_down'),
        icon: '↓',
        action: () => this.moveWidget(widgetId, 1),
      },
      {
        label:
          config.size === 'compact'
            ? this.context.t('widget_expand')
            : this.context.t('widget_compact'),
        icon: config.size === 'compact' ? '⬜' : '▪',
        action: () => this.toggleSize(widgetId),
      },
      { label: this.context.t('widget_hide'), icon: '✕', action: () => this.hideWidget(widgetId) },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { divider: true } as any,
      {
        label: this.context.t('widget_manage_all'),
        icon: '⚙',
        action: () => this.context.navigate('settings', 'home-layout'),
      },
    ];

    items.forEach((item) => {
      if (item.divider) {
        const hr = document.createElement('div');
        hr.style.cssText = 'height:1px;background:var(--border);margin:4px 0;';
        menu.appendChild(hr);
        return;
      }
      const btn = document.createElement('button');
      btn.style.cssText = `
        display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;
        background:transparent;cursor:pointer;font-size:13px;color:var(--text-primary);
        text-align:${this.context.isRtl ? 'right' : 'left'};transition:background 150ms;
      `;
      btn.innerHTML = `<span style="width:18px;text-align:center;font-size:14px;">${item.icon}</span><span>${item.label}</span>`;
      btn.addEventListener('mouseenter', () => {
        btn.style.background = 'var(--bg-secondary)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = 'transparent';
      });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        item.action();
        this.closeMenu();
      });
      menu.appendChild(btn);
    });

    const wrapper = anchor.parentElement;
    if (wrapper) {
      // Bump the open wrapper above all sibling widgets so the menu is not
      // covered by the next widget-card painted later in DOM order.
      (wrapper as HTMLElement).style.zIndex = '50';
      (wrapper as HTMLElement).style.position = 'relative';
      wrapper.appendChild(menu);
    }
  }

  private closeMenu(): void {
    this.activeMenuId = null;
    document.querySelectorAll('.widget-popup-menu').forEach((el) => {
      const w = el.parentElement as HTMLElement | null;
      if (w) w.style.zIndex = '';
      el.remove();
    });
  }

  private moveWidget(widgetId: string, direction: -1 | 1): void {
    const sorted = [...this.layout].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((c) => c.id === widgetId);
    if (idx < 0) return;

    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;

    // Swap orders
    const temp = sorted[idx].order;
    sorted[idx].order = sorted[targetIdx].order;
    sorted[targetIdx].order = temp;

    this.saveLayout();
    if (this.container) this.render(this.container);
  }

  private toggleSize(widgetId: string): void {
    const config = this.layout.find((c) => c.id === widgetId);
    if (!config) return;
    config.size = config.size === 'compact' ? 'expanded' : 'compact';
    this.saveLayout();
    if (this.container) this.render(this.container);
  }

  private hideWidget(widgetId: string): void {
    const config = this.layout.find((c) => c.id === widgetId);
    if (!config) return;
    config.visible = false;
    this.saveLayout();
    if (this.container) this.render(this.container);
  }

  getHiddenWidgets(): Array<{ id: string; label: Record<string, string>; icon: string }> {
    const hidden: Array<{ id: string; label: Record<string, string>; icon: string }> = [];
    this.layout.forEach((config) => {
      if (!config.visible) {
        const widget = this.widgets.get(config.id);
        if (widget) {
          hidden.push({ id: widget.id, label: widget.label, icon: widget.icon });
        }
      }
    });
    return hidden;
  }

  showWidget(widgetId: string): void {
    const config = this.layout.find((c) => c.id === widgetId);
    if (!config) return;
    config.visible = true;
    this.saveLayout();
    if (this.container) this.render(this.container);
  }

  getLayout(): WidgetConfig[] {
    return [...this.layout].sort((a, b) => a.order - b.order);
  }

  applyPreset(preset: WidgetPreset): void {
    this.layout = preset.configs.map((c) => ({ ...c }));
    this.saveLayout();
    if (this.container) this.render(this.container);
  }

  private loadLayout(): void {
    try {
      const raw = localStorage.getItem(LS_LAYOUT_KEY);
      if (raw) {
        this.layout = JSON.parse(raw) as WidgetConfig[];
      }
    } catch {
      /* ignore */
    }
  }

  private saveLayout(): void {
    try {
      localStorage.setItem(LS_LAYOUT_KEY, JSON.stringify(this.layout));
    } catch {
      /* ignore */
    }
  }

  destroy(): void {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = null;
    }
    this.widgets.forEach((widget) => {
      try {
        widget.destroy?.();
      } catch {
        /* ignore */
      }
    });
    this.widgets.clear();
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}
